import type { NonIndexRouteObject, Params } from 'react-router-dom'

import cx from 'classnames'
import React from 'react'
import { useSelector } from 'react-redux'
import { Link, matchRoutes } from 'react-router-dom'
import { useLocation } from 'react-use'

import { igaoPages } from '../igao/utils'

import './styles.scss'

interface Breadcrumb {
  path: string
  label: string
}

interface RouteObjectWithBreadcrumbs extends NonIndexRouteObject {
  children?: RouteObjectWithBreadcrumbs[]
  breadcrumbs?: Array<Breadcrumb | string> | string
}

function defaultPathSegmentTransform (segment: string): string {
  return segment
    .replace(/(?:_|-|\s)+/g, ' ')
    .replace(/\w\S*/g, c => `${c.substring(0, 1).toUpperCase()}${c.substring(1)}`)
}

export function substituteParams (path: string, params: Params = {}): string {
  return path
    .replace(/:(\w+)/g, (substring, key) => params[key] ?? key)
}

let routes: RouteObjectWithBreadcrumbs[] = [
  {
    path: '/',
    breadcrumbs: 'Dashboard',
    children: [
      {
        path: 'admin',
        breadcrumbs: 'Admin',
        children: [
          {
            path: 'receivers',
            breadcrumbs: 'External Recepients'
          },
          {
            path: 'behavioral',
            breadcrumbs: 'Behavioral Health History'
          },
          {
            path: 'youturn',
            breadcrumbs: 'YouTurn History'
          },
          {
            path: 'config',
            breadcrumbs: 'Configuration'
          },
          {
            path: 'orphan-igaos',
            breadcrumbs: 'Orphan IGAOs'
          },
          {
            path: 'igao',
            breadcrumbs: 'Dynamic IGAOs',
            children: [
              {
                path: '',
                // @ts-expect-error // TODO: fix this
                index: true,
                breadcrumbs: []
              },
              ...igaoPages.map(page => ({
                path: page.id,
                breadcrumbs: page.name
              }))
            ]
          }
        ]
      },
      {
        path: 'patient/:clientName/:id',
        breadcrumbs: [
          {
            path: '/patient/:clientName/:id/patientFolder',
            label: 'Patient Folder'
          }
        ],
        children: [
          {
            path: 'patientFolder',
            breadcrumbs: []
          },
          {
            path: 'report/:reportId/monthlySummary',
            breadcrumbs: 'Care Management Summary'
          },
          {
            path: 'report/:reportId/apsReport',
            breadcrumbs: 'APS Report'
          },
          {
            path: 'report/:reportId/researchReviewReport',
            breadcrumbs: 'Research Report'
          },
          {
            path: 'report/:reportId/encounterSummary',
            breadcrumbs: 'Encounter Summary'
          },
          {
            path: 'report/:reportId/carePlan',
            breadcrumbs: 'Care Plan'
          },
          {
            path: 'report/:reportId/patientSummary',
            breadcrumbs: 'Patient Summary'
          },
          {
            path: 'report/:reportId/patientBrief',
            breadcrumbs: 'Patient Brief'
          },
          {
            path: 'report/:reportId/grandRounds',
            breadcrumbs: 'Grand Rounds'
          },
          {
            path: 'report/:reportId/:reportType',
            breadcrumbs: 'Report'
          },
          {
            path: 'assessment',
            breadcrumbs: 'Encounter',
            children: [
              {
                path: 'igao',
                breadcrumbs: 'Care Plan'
              },
              {
                path: 'claims-history',
                breadcrumbs: 'Claims History'
              },
              {
                path: 'standardcsr',
                breadcrumbs: 'CSR'
              },
              {
                path: 'standarddemographicspatient',
                breadcrumbs: 'Patient'
              },
              {
                path: 'standarddemographicsinsurance',
                breadcrumbs: 'Insurance'
              },
              {
                path: 'standarddemographicshomeassessment',
                breadcrumbs: 'Home Assessment'
              },
              {
                path: 'standardsocialhistorytribal',
                breadcrumbs: 'Tribal'
              },
              {
                path: 'standarddemographicssupport',
                breadcrumbs: 'Family, Support'
              },
              {
                path: 'standarddemographicsmedical',
                breadcrumbs: 'Provider Network'
              },
              {
                path: 'standarddemographicscareteam',
                breadcrumbs: 'Care Team'
              },
              {
                path: 'standardmedicaldiagnosis',
                breadcrumbs: 'Diagnosis'
              },
              {
                path: 'standardmedicalevents',
                breadcrumbs: 'Events'
              },
              {
                path: 'standardmaternity',
                breadcrumbs: 'Maternity'
              },
              {
                path: 'standardsocialhistorylifestyle',
                breadcrumbs: 'Lifestyle'
              },
              {
                path: 'standardsocialhistoryresources',
                breadcrumbs: 'Resources'
              },
              {
                path: 'standardsocialhistorysubstance',
                breadcrumbs: 'Substance Use'
              },
              {
                path: 'standardallergies',
                breadcrumbs: 'Allergies'
              },
              {
                path: 'standardbehavioral',
                breadcrumbs: 'Behavioral Health'
              },
              {
                path: 'standardmedications',
                breadcrumbs: 'Medications'
              },
              {
                path: 'standardvitals',
                breadcrumbs: 'Vitals'
              },
              {
                path: 'standardsymptomsesas',
                breadcrumbs: 'ESAS+'
              },
              {
                path: 'standardsymptomsros',
                breadcrumbs: 'Review of Systems'
              },
              {
                path: 'standardpeexam',
                breadcrumbs: 'Physical Exam'
              },
              {
                path: 'standarddiagnostics',
                breadcrumbs: 'Diagnostics'
              },
              {
                path: 'standardgoals',
                breadcrumbs: 'Goals'
              },
              {
                path: 'utilizationexpense',
                breadcrumbs: 'Utilization & Expense'
              },
              {
                path: 'standardexpectations',
                breadcrumbs: 'Expectations'
              },
              {
                path: 'standardgoalschoices',
                breadcrumbs: 'Resuscitation Choice'
              },
              {
                path: 'encountersummary',
                breadcrumbs: 'Encounter Summary'
              },
              {
                path: 'encounterhistory',
                breadcrumbs: 'Encounter History'
              },
              {
                path: 'grandrounds',
                breadcrumbs: 'Grand Rounds Report'
              },
              {
                path: 'standarddocuments',
                breadcrumbs: 'Documents'
              },
              {
                path: 'uba',
                breadcrumbs: 'UBACARE Orders',
                children: [
                  {
                    path: 'new-user',
                    breadcrumbs: 'New User'
                  },
                  {
                    path: 'edit-user',
                    breadcrumbs: 'Edit User'
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]

interface GlobalState {
  messages?: {
    breadcrumbName?: string
  }
}

export default function Breadcrumbs (): JSX.Element {
  let { pathname } = useLocation()

  let matches = matchRoutes(routes, pathname ?? '') ?? []

  let patientName = useSelector<GlobalState, string>(state => state?.messages?.breadcrumbName || 'Patient Folder')

  if (!matches.length) {
    return (
      <FallbackBreadcrumbs />
    )
  }

  let breadcrumbs = matches.reduce<Breadcrumb[]>((result, match) => {
    let path = match.pathname
    let route = match.route
    let params = { ...match.params, patientName }

    if (Array.isArray(route.breadcrumbs)) {
      return [
        ...result,
        ...route.breadcrumbs.map(breadcrumb => {
          if (typeof breadcrumb === 'string') {
            return {
              path,
              label: substituteParams(breadcrumb, params)
            }
          }

          return {
            path: substituteParams(breadcrumb.path, params),
            label: substituteParams(breadcrumb.label, params)
          }
        })
      ]
    }

    if (typeof route.breadcrumbs === 'string') {
      return [
        ...result,
        {
          path,
          label: route.breadcrumbs
        }
      ]
    }

    return result
  }, [])

  return (
    <BreadcrumbsRenderer breadcrumbs={breadcrumbs} />
  )
}

interface BreadcrumbsRendererProps {
  breadcrumbs: Breadcrumb[]
}

function BreadcrumbsRenderer ({ breadcrumbs }: BreadcrumbsRendererProps): JSX.Element {
  return (
    <div className='breadcrumbs-row'>
      <div className='breadcrumbs'>
        {breadcrumbs.map((breadcrumb, i, a) => (
          <React.Fragment key={breadcrumb.path}>
            {i === a.length - 1 && (
              <span className={cx('text-base text-[#B2B2B2] font-normal', { 'breadcrumbs-root': i === 0 })}>
                {breadcrumb.label}
              </span>
            )}

            {i !== a.length - 1 && (
              <Link
                to={breadcrumb.path}
                className={cx('text-base text-[#2F7DE1] font-normal', { 'breadcrumbs-root': i === 0 })}
              >
                {breadcrumb.label}
              </Link>
            )}

            {i !== a.length - 1 && (
              <span className='text-base text-[#B2B2B2]'> / </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

function FallbackBreadcrumbs (): JSX.Element | null {
  let { pathname } = useLocation()

  if (!pathname) {
    return null
  }

  let breadcrumbs: Breadcrumb[] = pathname
    .replace(/^\//g, '')
    .split('/')
    .map((segment, i, a) => ({
      label: defaultPathSegmentTransform(segment),
      path: `/${a.slice(0, i + 1).join('/')}`
    }))
    .map((location) => location.label === '' && location.path === '/'
      ? {
        ...location,
        label: 'Dashboard',
        pathname: '/'
      }
      : location
    )

  if (breadcrumbs.length > 0 && breadcrumbs[0]?.path !== '/') {
    breadcrumbs.unshift({
      label: 'Dashboard',
      path: '/'
    })
  }

  return (
    <BreadcrumbsRenderer breadcrumbs={breadcrumbs} />
  )
}
